import React, { useState } from 'react';
import FavoriteButton from './FavoriteButton';
import FacilityNotes from './FacilityNotes';
import InsuranceSelect, { parseInsurance } from './InsuranceSelect';
import { useAuth } from '../context/AuthContext';
import { updateMyFacility } from '../api';

// MH inpatient bed types in display order
const MH_BEDS = [
  { field: 'beds_eval_treatment_adult', label: 'Eval & Treatment (Adult)' },
  { field: 'beds_eval_treatment_youth', label: 'Eval & Treatment (Youth)' },
  { field: 'beds_eval_treatment_clip',  label: 'Eval & Treatment (CLIP)' },
  { field: 'beds_crisis_stabilization', label: 'Crisis Stabilization' },
  { field: 'beds_intensive_bh',         label: 'Intensive BH Treatment' },
  { field: 'beds_competency_restoration', label: 'Competency Restoration' },
  { field: 'beds_mh_peer_respite',      label: 'MH Peer Respite' },
];

// SUD inpatient bed types in display order
const SUD_BEDS = [
  { field: 'beds_withdrawal_adult',        label: 'Withdrawal Management (Adult)' },
  { field: 'beds_withdrawal_youth',        label: 'Withdrawal Management (Youth)' },
  { field: 'beds_secure_withdrawal_adult', label: 'Secure Withdrawal (Adult)' },
  { field: 'beds_secure_withdrawal_youth', label: 'Secure Withdrawal (Youth)' },
  { field: 'beds_res_sud_adult',           label: 'Residential SUD (Adult)' },
  { field: 'beds_res_sud_youth',           label: 'Residential SUD (Youth)' },
];

const GENDER_OPTIONS = [
  { value: '',               label: 'Not specified' },
  { value: 'All genders',   label: 'All genders' },
  { value: 'Male only',     label: 'Male only' },
  { value: 'Female only',   label: 'Female only' },
];

const ALL_BED_TYPES = [
  { key: 'beds_eval_treatment_adult',    label: 'Eval & Treatment (Adult)' },
  { key: 'beds_eval_treatment_youth',    label: 'Eval & Treatment (Youth)' },
  { key: 'beds_eval_treatment_clip',     label: 'Eval & Treatment (CLIP)' },
  { key: 'beds_crisis_stabilization',    label: 'Crisis Stabilization' },
  { key: 'beds_intensive_bh',            label: 'Intensive BH Treatment' },
  { key: 'beds_competency_restoration',  label: 'Competency Restoration' },
  { key: 'beds_mh_peer_respite',         label: 'MH Peer Respite' },
  { key: 'beds_withdrawal_adult',        label: 'Withdrawal Mgmt (Adult)' },
  { key: 'beds_withdrawal_youth',        label: 'Withdrawal Mgmt (Youth)' },
  { key: 'beds_secure_withdrawal_adult', label: 'Secure Withdrawal (Adult)' },
  { key: 'beds_secure_withdrawal_youth', label: 'Secure Withdrawal (Youth)' },
  { key: 'beds_res_sud_adult',           label: 'Residential SUD (Adult)' },
  { key: 'beds_res_sud_youth',           label: 'Residential SUD (Youth)' },
];

const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400';
const textareaCls = `${inputCls} resize-none`;

function ModalSection({ title, children }) {
  return (
    <div className="pt-5 first:pt-0">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3 pb-1 border-b border-gray-100 dark:border-gray-700">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function ModalField({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

function formatPhone(raw) {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw; // return as-is if format is unrecognized
}

function hasText(value) {
  return typeof value === 'string' && value.trim() !== '';
}

function parseSelfReportedBeds(raw) {
  if (!raw) return {};
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

const STALE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

function formatUpdatedAgo(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffMin = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

function BedRow({ label, licensed, available }) {
  const hasAvailable = typeof available === 'number';
  return (
    <div className="flex justify-between items-center py-0.5 gap-3">
      <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
      <span className="flex items-center gap-2 ml-4 whitespace-nowrap">
        {hasAvailable && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
            available > 0
              ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800'
              : 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600'
          }`}>
            {available} available
          </span>
        )}
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
          {licensed}
          {hasAvailable && <span className="text-xs font-normal text-gray-400 ml-1">licensed</span>}
        </span>
      </span>
    </div>
  );
}

function BedSection({ title, beds, facility, accentClass, selfReported }) {
  const activeBeds = beds.filter((b) => (facility[b.field] || 0) > 0);
  if (activeBeds.length === 0) return null;

  return (
    <div className="mt-3">
      <div className={`inline-block text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded mb-2 ${accentClass}`}>
        {title}
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {activeBeds.map((b) => (
          <BedRow
            key={b.field}
            label={b.label}
            licensed={facility[b.field]}
            available={typeof selfReported[b.field] === 'number' ? selfReported[b.field] : undefined}
          />
        ))}
      </div>
    </div>
  );
}

function EditFacilityModal({ facility, onSave, onClose }) {
  const [form, setForm] = useState({
    agency_name:             facility.agency_name             || '',
    contact_name:            facility.contact_name            || '',
    contact_title:           facility.contact_title           || '',
    contact_phone:           facility.contact_phone           || '',
    contact_email:           facility.contact_email           || '',
    physical_street:         facility.physical_street         || '',
    physical_city:           facility.physical_city           || '',
    physical_zip:            facility.physical_zip            || '',
    website_url:             facility.website_url             || '',
    phone_number:            facility.phone_number            || '',
    gender_served:           facility.gender_served           || '',
    description:             facility.description             || '',
    languages_spoken:        facility.languages_spoken        || '',
    insurance_accepted:      parseInsurance(facility.insurance_accepted),
    diagnoses_accepted:      facility.diagnoses_accepted      || '',
    referral_requirements:   facility.referral_requirements   || '',
    is_wheelchair_accessible: !!facility.is_wheelchair_accessible,
    is_ada_compliant:         !!facility.is_ada_compliant,
    has_telehealth:           !!facility.has_telehealth,
    is_accepting_referrals:   !!facility.is_accepting_referrals,
    serves_ppw:               !!facility.serves_ppw,
    is_verified:              !!facility.is_verified,
  });

  const [bedAvailability, setBedAvailability] = useState(() => {
    if (!facility.self_reported_beds) return {};
    try { return JSON.parse(facility.self_reported_beds); } catch { return {}; }
  });

  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState('');

  const set = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const setBed = (k) => (e) => {
    const val = e.target.value === '' ? undefined : Number(e.target.value);
    setBedAvailability((b) => {
      const next = { ...b };
      if (val === undefined) delete next[k];
      else next[k] = val;
      return next;
    });
  };

  async function handleSave() {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const payload = {
        ...form,
        facility_id:              facility.id,
        insurance_accepted:       form.insurance_accepted.length > 0
          ? JSON.stringify(form.insurance_accepted)
          : '',
        is_wheelchair_accessible: form.is_wheelchair_accessible ? 1 : 0,
        is_ada_compliant:         form.is_ada_compliant ? 1 : 0,
        has_telehealth:           form.has_telehealth ? 1 : 0,
        is_accepting_referrals:   form.is_accepting_referrals ? 1 : 0,
        serves_ppw:               form.serves_ppw ? 1 : 0,
      };
      if (facility.has_inpatient && Object.keys(bedAvailability).length > 0) {
        const filteredBeds = Object.fromEntries(
          Object.entries(bedAvailability).filter(([k, v]) =>
            Number(facility[k] || 0) > 0 && v !== undefined && v !== null && v !== ''
          )
        );
        if (Object.keys(filteredBeds).length > 0) {
          payload.self_reported_beds = JSON.stringify(filteredBeds);
        }
      }
      await updateMyFacility(payload);
      // Build updated fields to reflect in the card
      const updates = {
        ...form,
        insurance_accepted:       form.insurance_accepted.length > 0
          ? JSON.stringify(form.insurance_accepted)
          : '',
        is_wheelchair_accessible: form.is_wheelchair_accessible ? 1 : 0,
        is_ada_compliant:         form.is_ada_compliant ? 1 : 0,
        has_telehealth:           form.has_telehealth ? 1 : 0,
        is_accepting_referrals:   form.is_accepting_referrals ? 1 : 0,
        serves_ppw:               form.serves_ppw ? 1 : 0,
      };
      if (payload.self_reported_beds) updates.self_reported_beds = payload.self_reported_beds;
      updates.self_reported_updated_at = new Date().toISOString();
      setSaved(true);
      setTimeout(() => onSave(updates), 800);
    } catch (err) {
      setError(err?.response?.data?.error || 'Save failed. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">Edit Facility</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{facility.agency_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 -mt-1 -mr-1">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-6 py-5 flex-1 space-y-5">

          <ModalSection title="Facility Name">
            <ModalField label="Agency Name">
              <input type="text" value={form.agency_name} onChange={set('agency_name')} className={inputCls} />
            </ModalField>
          </ModalSection>

          <ModalSection title="Point of Contact">
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="Name">
                <input type="text" value={form.contact_name} onChange={set('contact_name')} className={inputCls} placeholder="Jane Doe" />
              </ModalField>
              <ModalField label="Title">
                <input type="text" value={form.contact_title} onChange={set('contact_title')} className={inputCls} placeholder="Intake Coordinator" />
              </ModalField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="Direct Phone">
                <input type="text" value={form.contact_phone} onChange={set('contact_phone')} className={inputCls} placeholder="(360) 555-0100" />
              </ModalField>
              <ModalField label="Email">
                <input type="email" value={form.contact_email} onChange={set('contact_email')} className={inputCls} placeholder="intake@agency.org" />
              </ModalField>
            </div>
          </ModalSection>

          <ModalSection title="Address">
            <ModalField label="Street">
              <input type="text" value={form.physical_street} onChange={set('physical_street')} className={inputCls} />
            </ModalField>
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="City">
                <input type="text" value={form.physical_city} onChange={set('physical_city')} className={inputCls} />
              </ModalField>
              <ModalField label="Zip">
                <input type="text" value={form.physical_zip} onChange={set('physical_zip')} className={inputCls} />
              </ModalField>
            </div>
          </ModalSection>

          <ModalSection title="Profile">
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="Website">
                <input type="url" value={form.website_url} onChange={set('website_url')} className={inputCls} placeholder="https://www.agency.org" />
              </ModalField>
              <ModalField label="Main Phone">
                <input type="text" value={form.phone_number} onChange={set('phone_number')} className={inputCls} placeholder="(360) 555-0100" />
              </ModalField>
            </div>
            <ModalField label="Program Description">
              <textarea value={form.description} onChange={set('description')} rows={3} className={textareaCls}
                placeholder="A few sentences about your approach, philosophy, and specialties…" />
            </ModalField>
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="Languages Spoken">
                <input type="text" value={form.languages_spoken} onChange={set('languages_spoken')} className={inputCls} placeholder="e.g. English, Spanish" />
              </ModalField>
              <ModalField label="Insurance Accepted">
                <InsuranceSelect
                  selected={form.insurance_accepted}
                  onChange={(arr) => setForm((f) => ({ ...f, insurance_accepted: arr }))}
                  inputCls={inputCls}
                />
              </ModalField>
            </div>
          </ModalSection>

          <ModalSection title="Services">
            <ModalField label="Gender Served">
              <select value={form.gender_served} onChange={set('gender_served')} className={`${inputCls} bg-white`}>
                {GENDER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {(form.gender_served === 'Female only' || form.gender_served === 'All genders' || form.gender_served === '') && (
                <label className="flex items-center gap-2 cursor-pointer mt-2">
                  <input type="checkbox" checked={form.serves_ppw} onChange={set('serves_ppw')}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span className="text-xs text-gray-700 dark:text-gray-300">Serves pregnant / parenting women (PPW)</span>
                </label>
              )}
            </ModalField>
            <ModalField label="Diagnoses Accepted">
              <textarea value={form.diagnoses_accepted} onChange={set('diagnoses_accepted')} rows={2} className={textareaCls}
                placeholder="What diagnoses or conditions you can and can't treat…" />
            </ModalField>
            <ModalField label="Referral Requirements">
              <textarea value={form.referral_requirements} onChange={set('referral_requirements')} rows={2} className={textareaCls}
                placeholder="What paperwork, assessments, or documentation referrers need to send…" />
            </ModalField>
          </ModalSection>

          {/* Admin-only: verification toggle */}
          <ModalSection title="Admin Controls">
            <label className="flex items-center gap-3 cursor-pointer">
              <div className={`relative w-10 h-6 rounded-full transition-colors ${form.is_verified ? 'bg-teal-500' : 'bg-gray-300'}`}>
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_verified ? 'translate-x-5' : 'translate-x-1'}`} />
              </div>
              <input type="checkbox" checked={form.is_verified} onChange={set('is_verified')} className="sr-only" />
              <span className="text-sm font-medium">
                {form.is_verified
                  ? <span className="text-teal-700">Verified — badge shown on card</span>
                  : <span className="text-gray-500">Not verified</span>}
              </span>
            </label>
          </ModalSection>

          <ModalSection title="Accessibility & Options">
            <div className="space-y-2">
              {[
                { key: 'is_wheelchair_accessible', label: 'Wheelchair accessible' },
                { key: 'is_ada_compliant',         label: 'ADA compliant' },
                { key: 'has_telehealth',            label: 'Telehealth available' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form[key]} onChange={set(key)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </ModalSection>

          <ModalSection title="Availability">
            <label className="flex items-center gap-3 cursor-pointer">
              <div className={`relative w-10 h-6 rounded-full transition-colors ${form.is_accepting_referrals ? 'bg-green-500' : 'bg-gray-300'}`}>
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_accepting_referrals ? 'translate-x-5' : 'translate-x-1'}`} />
              </div>
              <input type="checkbox" checked={form.is_accepting_referrals} onChange={set('is_accepting_referrals')} className="sr-only" />
              <span className="text-sm font-medium">
                {form.is_accepting_referrals
                  ? <span className="text-green-700">Currently accepting referrals</span>
                  : <span className="text-gray-500">Not currently accepting referrals</span>}
              </span>
            </label>

            {facility.has_inpatient && (
              <div className="mt-3 space-y-2">
                <p className="text-xs font-medium text-gray-600">Self-reported bed availability</p>
                <p className="text-xs text-gray-400">Leave blank for bed types that don't apply.</p>
                {ALL_BED_TYPES.filter((b) => Number(facility[b.key] || 0) > 0).map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-gray-600 flex-1">{label}</span>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <span>Licensed: {facility[key] || 0}</span>
                      <span>·</span>
                      <span>Available:</span>
                      <input
                        type="number" min={0} max={facility[key] || 99}
                        value={bedAvailability[key] ?? ''}
                        onChange={setBed(key)}
                        className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="—"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ModalSection>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex-shrink-0">
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || saved}
              className={`px-5 py-2 text-sm rounded-lg font-semibold transition-colors ${
                saved
                  ? 'bg-green-600 text-white'
                  : saving
                    ? 'bg-blue-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// isFavorited: pass true when rendering from the Favorites page
export default function FacilityCard({ facility, isFavorited = false, addedByColleague = null }) {
  const { user } = useAuth();
  const isAdmin = user?.account_type === 'admin';

  const [editOpen, setEditOpen] = useState(false);
  const [localFacility, setLocalFacility] = useState(facility);

  const phone = formatPhone(localFacility.phone_number);
  const hasStreet = hasText(localFacility.physical_street);
  const hasWebsite = hasText(localFacility.website_url);
  const hasGenderServed = hasText(localFacility.gender_served);
  const hasDescription = hasText(localFacility.description);
  const hasLanguages = hasText(localFacility.languages_spoken);
  const insuranceList = parseInsurance(localFacility.insurance_accepted);
  const hasInsurance = insuranceList.length > 0;
  const hasContactName = hasText(localFacility.contact_name);
  const hasFeatureBadges = Boolean(hasWebsite || hasGenderServed || localFacility.has_telehealth || localFacility.is_wheelchair_accessible || localFacility.is_ada_compliant);
  const isCoOccurring = Boolean(localFacility.is_co_occurring);
  const servesPpw     = Boolean(localFacility.serves_ppw);
  const hasOutpatient = Boolean(localFacility.has_outpatient);

  const hasMhBeds  = MH_BEDS.some((b) => (localFacility[b.field] || 0) > 0);
  const hasSudBeds = SUD_BEDS.some((b) => (localFacility[b.field] || 0) > 0);

  const selfReported = parseSelfReportedBeds(localFacility.self_reported_beds);
  const isAccepting = Boolean(localFacility.is_accepting_referrals);
  const isVerified = Boolean(localFacility.is_verified);
  const updatedAt = localFacility.self_reported_updated_at;
  const updatedAgo = updatedAt ? formatUpdatedAgo(updatedAt) : null;
  const isStale = updatedAt ? (Date.now() - new Date(updatedAt).getTime()) > STALE_AFTER_MS : false;
  const showReferralBadge = isAccepting || (isVerified && updatedAt);

  function handleSave(updates) {
    setLocalFacility((prev) => ({ ...prev, ...updates }));
    setEditOpen(false);
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow">
      {/* Header row: name + badges + edit button + favorite button */}
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 leading-snug">
          {localFacility.agency_name}
        </h3>
        <div className="flex items-center gap-2 flex-shrink-0">
          {facility.distance_miles != null && (
            <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap border border-green-200">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {facility.distance_miles} mi
            </span>
          )}
          {showReferralBadge && (
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap border ${
              isAccepting
                ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800'
                : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
            }`}>
              <span className={`w-2 h-2 rounded-full inline-block ${isAccepting ? 'bg-green-500' : 'bg-red-500'}`}></span>
              {isAccepting ? 'Accepting referrals' : 'Not accepting referrals'}
            </span>
          )}
          {localFacility.is_verified && (
            <span className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap border border-teal-200">
              <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Verified
            </span>
          )}
          {isCoOccurring && (
            <span className="inline-block bg-purple-100 text-purple-700 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
              Co-occurring
            </span>
          )}
          {servesPpw && (
            <span className="inline-block bg-pink-100 text-pink-700 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
              PPW
            </span>
          )}
          {isAdmin && (
            <button
              onClick={() => setEditOpen(true)}
              title="Edit facility info"
              className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            >
              {/* Pencil icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
            </button>
          )}
          <FavoriteButton
            facilityId={localFacility.id}
            initialFavorited={isFavorited}
            addedByColleague={addedByColleague}
          />
        </div>
      </div>

      {/* Location + phone */}
      <div className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 space-y-0.5">
        {hasStreet && (
          <p>{localFacility.physical_street}</p>
        )}
        <div className="flex flex-wrap items-center gap-x-3">
          <span>
            {[
              localFacility.physical_city,
              localFacility.physical_county ? `${localFacility.physical_county} County` : null,
              localFacility.physical_zip,
            ].filter(Boolean).join(', ')}
          </span>
          {phone && (
            <>
              <span className="text-gray-300">|</span>
              <a href={`tel:${phone}`} className="text-blue-600 hover:underline font-medium">
                {phone}
              </a>
            </>
          )}
        </div>
      </div>


      {/* Website + Gender + feature badges */}
      {hasFeatureBadges && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          {hasWebsite && (
            <a
              href={localFacility.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline font-medium"
            >
              Visit website →
            </a>
          )}
          {hasGenderServed && (
            <span className="inline-block bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium px-2.5 py-0.5 rounded-full">
              {localFacility.gender_served}
            </span>
          )}
          {localFacility.has_telehealth ? (
            <span className="inline-block bg-sky-50 text-sky-700 text-xs font-medium px-2.5 py-0.5 rounded-full">
              Telehealth
            </span>
          ) : null}
          {localFacility.is_wheelchair_accessible ? (
            <span className="inline-block bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium px-2.5 py-0.5 rounded-full">
              ♿ Wheelchair accessible
            </span>
          ) : null}
          {localFacility.is_ada_compliant ? (
            <span className="inline-block bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium px-2.5 py-0.5 rounded-full">
              ADA compliant
            </span>
          ) : null}
        </div>
      )}

      {/* Description */}
      {hasDescription && (
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{localFacility.description}</p>
      )}

      {/* Languages + Insurance */}
      {(hasLanguages || hasInsurance) && (
        <div className="mt-2 space-y-0.5">
          {hasLanguages && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-300">Languages: </span>
              {localFacility.languages_spoken}
            </p>
          )}
          {hasInsurance && (
            <div>
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Insurance accepted:</p>
              <div className="flex flex-wrap gap-1">
                {insuranceList.map((ins) => (
                  <span key={ins} className="inline-block bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs px-2 py-0.5 rounded-full border border-blue-200 dark:border-blue-700">
                    {ins}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contact person */}
      {hasContactName && (
        <div className="mt-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
          <p className="font-medium text-gray-800 dark:text-gray-200">
            {localFacility.contact_name}
            {localFacility.contact_title && <span className="font-normal text-gray-500"> · {localFacility.contact_title}</span>}
          </p>
          {localFacility.contact_phone && (
            <a href={`tel:${localFacility.contact_phone}`} className="block text-blue-600 hover:underline">
              {localFacility.contact_phone}
            </a>
          )}
          {localFacility.contact_email && (
            <a href={`mailto:${localFacility.contact_email}`} className="block text-blue-600 hover:underline truncate">
              {localFacility.contact_email}
            </a>
          )}
        </div>
      )}

      {/* Bed sections */}
      {(hasMhBeds || hasSudBeds) && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          {updatedAgo && (
            <div className={`flex items-center gap-1.5 text-xs mb-1 ${
              isStale ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'
            }`}>
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                Availability updated by facility {updatedAgo}
                {isStale && ' — may be out of date'}
              </span>
            </div>
          )}
          <BedSection
            title="Mental Health Inpatient"
            beds={MH_BEDS}
            facility={localFacility}
            accentClass="bg-blue-50 text-blue-700"
            selfReported={selfReported}
          />
          <BedSection
            title="SUD Inpatient"
            beds={SUD_BEDS}
            facility={localFacility}
            accentClass="bg-emerald-50 text-emerald-700"
            selfReported={selfReported}
          />
        </div>
      )}


      {/* Outpatient indicator */}
      {hasOutpatient && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
            <span className="w-2 h-2 rounded-full bg-teal-500 inline-block"></span>
            Outpatient services available
          </span>
        </div>
      )}

      {/* Notes (authenticated users only — FacilityNotes self-guards) */}
      <FacilityNotes facilityId={localFacility.id} />

      {/* Edit modal */}
      {editOpen && (
        <EditFacilityModal
          facility={localFacility}
          onSave={handleSave}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  );
}




